import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { meQuery } from "../../../api/queries";
import { Button } from "../../../ui/Button";
import { Loading } from "../../../ui/Spinner";
import { ErrorMessage } from "../../../ui/ErrorMessage";
import { deviceQuery } from "../page/queries";
import { devicePlansQuery, requestUpgrade } from "./queries";
import {
  nextStep,
  NOTE_MAX,
  planHistory,
  planIncludes,
  planInterval,
  planBelow,
  planLimit,
  planName,
  planPrice,
  planTitle,
  isLower,
  upgradeError,
  type Device,
  type DevicePlan,
} from "./plans";

/**
 * The plans sheet (device-plans DP-11 to DP-13, DP-20 to DP-27, DP-40, DP-41): every plan for this device's product
 * with what it includes, how often the device sends, the history and the price; Upgrade on the higher plans and
 * Downgrade on the lower ones for the Owner and Admins, then a short confirm with an optional note, then "Request
 * sent". Payment comes later through the same button (DP-23).
 */
export function PlansDialog({ orgId, device, onClose }: { orgId: string; device: Device; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const me = useQuery(meQuery());
  const dialog = useRef<HTMLDialogElement>(null);
  const ids = { title: useId(), note: useId() };
  const plans = useQuery(devicePlansQuery(orgId, device.id));
  const [chosen, setChosen] = useState<DevicePlan>();
  const [note, setNote] = useState("");
  const [sentTo, setSentTo] = useState<string>();

  useEffect(() => {
    const d = dialog.current;
    if (d && !d.open) d.showModal();
  }, []);

  const send = useMutation({
    mutationFn: async (p: DevicePlan) => nextStep(await requestUpgrade(orgId, device.id, p.id, note)),
    onSuccess: (next) => {
      if (next.kind === "checkout") {
        window.location.assign(next.url);
        return;
      }
      setSentTo(next.replyTo ?? me.data?.email ?? "");
      void qc.invalidateQueries({ queryKey: deviceQuery(orgId, device.id).queryKey });
    },
  });

  const name = device.name ?? device.device_id;
  const current = plans.data?.find((p) => p.current);
  const lang = i18n.language;
  const lower = (p: DevicePlan) => isLower(p, current);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (chosen && !send.isPending) send.mutate(chosen);
  }

  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      aria-labelledby={ids.title}
      className="m-auto w-full max-w-2xl rounded-lg p-0 shadow-xl backdrop:bg-black/40"
    >
      <form onSubmit={submit} className="space-y-4 p-6 text-sm">
        <h2 id={ids.title} className="text-lg font-semibold text-gray-900">
          {sentTo !== undefined
            ? t("plans.sent.title")
            : chosen
              ? t(lower(chosen) ? "plans.confirm.titleDown" : "plans.confirm.title", {
                  plan: planTitle(chosen, t),
                })
              : t("plans.sheetTitle", { device: name })}
        </h2>

        {sentTo !== undefined ? (
          <p role="status">{t("plans.sent.text", { email: sentTo })}</p>
        ) : chosen ? (
          <>
            <p>
              {t("plans.confirm.text", {
                device: name,
                id: device.device_id,
                plan: planTitle(chosen, t),
                price: planPrice(chosen, lang, t),
              })}
            </p>
            {lower(chosen) && <p>{t("plans.confirm.lower")}</p>}
            {me.data?.email && <p className="text-gray-800">{t("plans.confirm.reply", { email: me.data.email })}</p>}
            <div>
              <label htmlFor={ids.note} className="block font-medium">
                {t("plans.confirm.note")}
              </label>
              <textarea
                id={ids.note}
                rows={3}
                maxLength={NOTE_MAX}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5"
              />
            </div>
            {send.isError && (
              <p role="alert" className="text-red-800">
                {upgradeError(send.error, t)}
              </p>
            )}
          </>
        ) : plans.isPending ? (
          <Loading />
        ) : plans.isError ? (
          <ErrorMessage error={plans.error} onRetry={() => void plans.refetch()} />
        ) : (
          <>
            <ul className="grid gap-3 sm:grid-cols-2">
              {plans.data.map((p) => (
                <PlanCard
                  key={p.id}
                  plan={p}
                  below={planBelow(plans.data, p)}
                  lower={lower(p)}
                  managed={!!device.plan_managed_by}
                  lang={lang}
                  onUpgrade={() => setChosen(p)}
                />
              ))}
            </ul>
            <p className="text-gray-700">{t("plans.note.battery")}</p>
          </>
        )}

        <div className="flex justify-end gap-2">
          {chosen && sentTo === undefined ? (
            <>
              <Button type="button" variant="secondary" onClick={() => setChosen(undefined)}>
                {t("plans.back")}
              </Button>
              <Button type="submit" disabled={send.isPending}>
                {t("plans.confirm.send")}
              </Button>
            </>
          ) : (
            <Button type="button" variant="secondary" onClick={() => dialog.current?.close()}>
              {t("plans.close")}
            </Button>
          )}
        </div>
      </form>
    </dialog>
  );
}

function PlanCard({
  plan: p,
  below,
  lower,
  managed,
  lang,
  onUpgrade,
}: {
  plan: DevicePlan;
  below: Pick<DevicePlan, "id" | "name"> | undefined;
  lower: boolean;
  managed: boolean;
  lang: string;
  onUpgrade: () => void;
}) {
  const { t } = useTranslation();
  const name = planTitle(p, t);
  const price = planPrice(p, lang, t);
  const free = p.price?.month_cents === 0;
  const interval = planInterval(p.report_interval_s, t);
  const includes = planIncludes(p, t);
  return (
    <li
      className={`flex flex-col rounded-md border p-4 ${p.current ? "border-brand bg-brand/5" : "border-gray-200 bg-white"}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900">{name}</h3>
        {p.current && (
          <span className="rounded bg-brand px-1.5 py-0.5 text-xs font-medium text-white">{t("plans.current")}</span>
        )}
      </div>
      {/* Free's name already says its price (plans-statistics-polish PSP-02). */}
      {!free && <p className="mt-1 font-medium text-gray-900">{price}</p>}
      <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-gray-800">
        <dt>{t("plans.historyLabel")}</dt>
        <dd>{planHistory(p.id === "enterprise" && !p.custom ? null : p.visible_history_days, t)}</dd>
        {interval && (
          <>
            <dt>{t("plans.intervalLabel")}</dt>
            <dd>{interval}</dd>
          </>
        )}
        <dt>{t("plans.alarmsLabel")}</dt>
        <dd>{planLimit(p.limits.alarms, t)}</dd>
        <dt>{t("plans.automationsLabel")}</dt>
        <dd>{planLimit(p.limits.automations, t)}</dd>
        <dt>{t("plans.healthAlarmsLabel")}</dt>
        <dd>{t("plans.healthAlarms")}</dd>
      </dl>
      {below && !p.custom && includes.length > 0 && (
        <p className="mt-2 text-gray-800">{t("plans.everythingIn", { plan: planName(below.id, t, below.name) })}</p>
      )}
      <ul className="mt-1 list-disc pl-5 text-gray-800">
        {includes.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {p.requires?.min_devices && (
        <p className="mt-2 text-gray-700">{t("plans.requiresDevices", { count: p.requires.min_devices - 1 })}</p>
      )}
      <div className="mt-auto pt-3">
        {p.can_request ? (
          <Button type="button" className="w-full px-3 py-1.5" onClick={onUpgrade}>
            {free
              ? t("plans.downgradeFree", { plan: name })
              : t(lower ? "plans.downgrade" : "plans.upgrade", { plan: name, price })}
          </Button>
        ) : (
          !p.current && !managed && <p className="text-xs text-gray-700">{t("plans.askAdmin")}</p>
        )}
      </div>
    </li>
  );
}
